/**
 * @fileoverview Admin "Programs" surface for a single owner.
 *
 * Single screen showing this owner's vouchers, winback campaigns, points
 * config, and outstanding wash-credit liability. All admin-side calls pass
 * ?ownerId=X to the backend, which routes them through resolveOwnerForRequest.
 *
 * Admin can:
 *   • View + delete vouchers (with claimed-warning gate)
 *   • View + run / deactivate winback campaigns
 *   • View points config + outstanding wash credit
 *
 * Creating new vouchers / campaigns / etc. is still done from the owner
 * screens — admin can navigate there directly with ?ownerId=X appended.
 */

import React, { useCallback, useState } from 'react';
import { Alert, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { confirmDestructive } from '../../../../src/utils/confirm';
import { useLocalSearchParams, useRouter, useFocusEffect } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { api, apiGet } from '../../../../src/api/client';
import Card from '../../../../src/components/Card';
import Button from '../../../../src/components/Button';
import ContentContainer from '../../../../src/components/ContentContainer';
import { useToast } from '../../../../src/components/Toast';
import { useTheme } from '../../../../src/context/ThemeContext';
import { useNetwork } from '../../../../src/context/NetworkContext';
import type { ColorTheme } from '../../../../src/utils/theme';
import { spacing, fontSize, fontWeight, borderRadius } from '../../../../src/utils/theme';

interface VoucherRow {
  id: string; code: string;
  kind: string; discountKind: string; percentOff: number | null;
  tierName: string | null; recipientEmail: string | null;
  freeMinutes?: number | null; freeVendCents?: number | null; creditCents?: number | null;
  status: string; redemptionsUsed: number; redemptionLimit: number;
  source: string; expiresAt: string | null;
}
interface CampaignRow {
  id: string; name: string; lapsedDays: number;
  isActive: boolean; runFrequencyDays: number; lastRunAt: string | null;
  voucherTemplateJson: any;
}
interface PointsCfg {
  isEnabled: boolean; pointsPerDollar: number;
  redemptionTable: { points: number; tierName: string | null; freeMinutes: number | null }[];
  expirationMonths: number; customerVisibleName: string | null;
}
interface WashCreditOutstanding {
  totalOutstandingCents: number; customerCount: number;
  rows: { id: string; recipientEmail: string; balanceCents: number; expiresAt: string | null }[];
}

export default function AdminOwnerProgramsScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const router = useRouter();
  const toast = useToast();
  const { isOffline } = useNetwork();
  const { id: ownerId } = useLocalSearchParams<{ id: string }>();

  const [vouchers, setVouchers] = useState<VoucherRow[]>([]);
  const [campaigns, setCampaigns] = useState<CampaignRow[]>([]);
  const [pointsCfg, setPointsCfg] = useState<PointsCfg | null>(null);
  const [credit, setCredit] = useState<WashCreditOutstanding | null>(null);

  const ownerQS = `?ownerId=${encodeURIComponent(ownerId ?? '')}`;

  const fetchAll = useCallback(async () => {
    if (!ownerId) return;
    let failures = 0;
    const track = <T,>(p: Promise<T>, fallback: T): Promise<T> => p.catch(() => { failures++; return fallback; });
    try {
      const [v, c, pc, wco] = await Promise.all([
        track(apiGet<{ vouchers: VoucherRow[] }>(`/api/vouchers${ownerQS}`).then(r => r.vouchers), [] as VoucherRow[]),
        track(apiGet<{ campaigns: CampaignRow[] }>(`/api/winback/campaigns${ownerQS}`).then(r => r.campaigns), [] as CampaignRow[]),
        track(apiGet<PointsCfg | null>(`/api/owner/points/config${ownerQS}`), null),
        track(apiGet<WashCreditOutstanding | null>(`/api/owner/wash-credit/outstanding${ownerQS}`), null),
      ]);
      setVouchers(v);
      setCampaigns(c);
      setPointsCfg(pc);
      setCredit(wco);
      if (failures > 0 && !isOffline) {
        toast.show({ type: 'warning', title: 'Some programs didn’t load', message: `${failures} section${failures === 1 ? '' : 's'} failed.` });
      }
    } catch (err: any) {
      if (!isOffline) {
        toast.show({ type: 'error', title: 'Error', message: err.message });
      }
    }
  }, [ownerId, ownerQS, isOffline, toast]);

  useFocusEffect(useCallback(() => { fetchAll(); }, [fetchAll]));

  const handleDeleteVoucher = (v: VoucherRow) => {
    if (v.redemptionsUsed > 0) {
      Alert.alert('Voucher already used', `Used ${v.redemptionsUsed} time(s). Cannot delete.`, [{ text: 'OK' }]);
      return;
    }
    confirmDestructive(
      'Delete voucher?',
      'The redemption code will stop working immediately.',
      async () => {
        try {
          await api(`/api/vouchers/${v.id}${ownerQS}`, { method: 'DELETE' });
          toast.show({ type: 'success', title: 'Deleted' });
          fetchAll();
        } catch (err: any) {
          toast.show({ type: 'error', title: 'Error', message: err.message });
        }
      },
    );
  };

  const handleRunCampaign = async (campId: string) => {
    try {
      const res = await api<{ succeeded: number; failed: number; skipped: number }>(`/api/winback/campaigns/${campId}/run-now${ownerQS}`, { method: 'POST', body: {} });
      toast.show({ type: 'success', title: 'Campaign run', message: `Sent ${res.succeeded}, skipped ${res.skipped}` });
      fetchAll();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    }
  };

  const handleDeactivateCampaign = async (campId: string) => {
    confirmDestructive(
      'Deactivate this campaign?',
      'It will stop running on the daily cron.',
      async () => {
        try {
          await api(`/api/winback/campaigns/${campId}${ownerQS}`, { method: 'DELETE' });
          toast.show({ type: 'success', title: 'Deactivated' });
          fetchAll();
        } catch (err: any) {
          toast.show({ type: 'error', title: 'Error', message: err.message });
        }
      },
      'Deactivate',
    );
  };

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <ContentContainer>
        <Text style={styles.header}>Programs (admin view)</Text>
        <Text style={styles.subhead}>Acting on behalf of owner {ownerId}</Text>

        {/* Create actions */}
        <View style={styles.actionsRow}>
          {/* voucher-new covers free wash / % off / free minutes / $ credit
              (absorbed the former promo-new + comp-credit twins). */}
          <Button title="Issue Wash / Credit" size="sm" onPress={() => router.push(`/admin/owner/${ownerId}/programs/voucher-new` as never)} />
          <Button title="New QR" size="sm" variant="outline" onPress={() => router.push(`/admin/owner/${ownerId}/programs/qr-new` as never)} />
          <Button title="Winback" size="sm" variant="outline" onPress={() => router.push(`/admin/owner/${ownerId}/programs/winback` as never)} />
          <Button title="Edit Points" size="sm" variant="outline" onPress={() => router.push(`/admin/owner/${ownerId}/programs/points-edit` as never)} />
        </View>

        {/* Vouchers */}
        <Text style={styles.sectionTitle}>Vouchers ({vouchers.length})</Text>
        {vouchers.length === 0 && <Text style={styles.hint}>No vouchers issued.</Text>}
        {vouchers.slice(0, 30).map(v => {
          const isQr = v.source === 'gift_qr_single' || v.source === 'gift_qr_multi';
          return (
            <Card key={v.id} style={styles.card}>
              <View style={styles.row}>
                <View style={{ flex: 1 }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.xs }}>
                    <Ionicons name={isQr ? 'qr-code' : 'gift'} size={16} color={colors.primary} />
                    <Text style={styles.cardTitle}>{v.recipientEmail ?? (isQr ? 'QR voucher' : 'Voucher')}</Text>
                  </View>
                  <Text style={styles.cardSub}>
                    {v.discountKind === 'credit'
                      ? `$${((v.creditCents ?? 0) / 100).toFixed(2)} credit`
                      : v.discountKind === 'free_vend'
                      ? `$${((v.freeVendCents ?? 0) / 100).toFixed(2)} vending credit`
                      : v.discountKind === 'free_minutes'
                      ? `${v.freeMinutes ?? '?'} free minutes`
                      : v.discountKind === 'free_wash' || (v.discountKind === 'percent' && v.percentOff === 100)
                      ? `Free ${v.tierName ?? 'wash'}`
                      : `${v.percentOff}% off${v.tierName ? ` ${v.tierName}` : ''}`}
                    {' · '}{v.code}{' · '}{v.status}
                    {' · '}{v.redemptionsUsed}/{v.redemptionLimit} used
                  </Text>
                </View>
                {(v.status === 'active' || v.status === 'pending') && v.redemptionsUsed === 0 && (
                  <TouchableOpacity onPress={() => handleDeleteVoucher(v)} style={styles.deleteBtn}>
                    <Ionicons name="trash" size={18} color={colors.danger} />
                  </TouchableOpacity>
                )}
              </View>
            </Card>
          );
        })}

        {/* Winback campaigns */}
        <Text style={styles.sectionTitle}>Winback Campaigns ({campaigns.length})</Text>
        {campaigns.length === 0 && <Text style={styles.hint}>No automated winback rules.</Text>}
        {campaigns.map(c => (
          <Card key={c.id} style={styles.card}>
            <View style={styles.row}>
              <View style={{ flex: 1 }}>
                <Text style={styles.cardTitle}>{c.name}</Text>
                <Text style={styles.cardSub}>
                  {c.voucherTemplateJson?.percentOff}% off · {c.lapsedDays}d lapsed · every {c.runFrequencyDays}d
                </Text>
                <Text style={styles.cardSub}>Last run: {c.lastRunAt ? new Date(c.lastRunAt).toLocaleString() : 'never'}</Text>
              </View>
              <View style={{ gap: spacing.xs }}>
                <Button title="Run" size="sm" onPress={() => handleRunCampaign(c.id)} />
                {c.isActive && (
                  <Button title="Deactivate" size="sm" variant="outline" onPress={() => handleDeactivateCampaign(c.id)} />
                )}
              </View>
            </View>
          </Card>
        ))}

        {/* Points config */}
        <Text style={styles.sectionTitle}>Points Program</Text>
        {pointsCfg ? (
          <Card style={styles.card}>
            <View style={styles.kvRow}><Text style={styles.kvLabel}>Enabled</Text><Text style={styles.kvValue}>{pointsCfg.isEnabled ? 'Yes' : 'No'}</Text></View>
            <View style={styles.kvRow}><Text style={styles.kvLabel}>Earn rate</Text><Text style={styles.kvValue}>{pointsCfg.pointsPerDollar} per $1</Text></View>
            <View style={styles.kvRow}><Text style={styles.kvLabel}>Expires (months)</Text><Text style={styles.kvValue}>{pointsCfg.expirationMonths}</Text></View>
            <View style={styles.kvRow}><Text style={styles.kvLabel}>Program name</Text><Text style={styles.kvValue}>{pointsCfg.customerVisibleName ?? '—'}</Text></View>
            <View style={styles.kvRow}><Text style={styles.kvLabel}>Redemption options</Text><Text style={styles.kvValue}>{pointsCfg.redemptionTable.length}</Text></View>
          </Card>
        ) : (
          <Text style={styles.hint}>Not configured.</Text>
        )}

        {/* Wash credit liability */}
        <Text style={styles.sectionTitle}>Wash Credit Liability</Text>
        {credit ? (
          <Card style={styles.card}>
            <View style={styles.kvRow}><Text style={styles.kvLabel}>Total outstanding</Text><Text style={styles.kvValue}>${(credit.totalOutstandingCents / 100).toFixed(2)}</Text></View>
            <View style={styles.kvRow}><Text style={styles.kvLabel}>Customers</Text><Text style={styles.kvValue}>{credit.customerCount}</Text></View>
          </Card>
        ) : (
          <Text style={styles.hint}>No outstanding credit.</Text>
        )}

        <View style={{ height: spacing.xxl }} />
      </ContentContainer>
    </ScrollView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  content: { padding: spacing.md },
  header: { fontSize: fontSize.xl, fontWeight: fontWeight.bold as any, color: c.text },
  subhead: { fontSize: fontSize.xs, color: c.textSecondary, marginBottom: spacing.lg, fontFamily: 'monospace' },
  sectionTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold as any, color: c.text, marginTop: spacing.md, marginBottom: spacing.sm },
  card: { padding: spacing.md, marginBottom: spacing.sm },
  row: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  cardTitle: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold as any, color: c.text },
  cardSub: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 2 },
  kvRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: spacing.xs },
  kvLabel: { color: c.textSecondary, fontSize: fontSize.sm },
  kvValue: { color: c.text, fontSize: fontSize.sm, fontWeight: fontWeight.semibold as any },
  hint: { color: c.textLight, fontStyle: 'italic', fontSize: fontSize.sm, marginVertical: spacing.sm },
  deleteBtn: { padding: spacing.sm },
  actionsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs, marginBottom: spacing.md },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
